import { FRUITFUL_INSTITUTION_NAME } from '@fruitful/types';
import {
    AppleIcon,
    BananaIcon,
    CherryIcon,
    CitrusIcon,
    GrapeIcon,
    NutIcon,
} from 'lucide-react-native';
import React, { useState } from 'react';
import { XStack, YStack } from 'tamagui';

import { MoneySystemIcon } from '../../..';
import CalendarDollarSignIcon from '../../components/icons/custom/CalendarDollarSign.svg';
import CircleNumberFiveIcon from '../../components/icons/custom/CircleNumberFive.svg';
import CircleNumberFourIcon from '../../components/icons/custom/CircleNumberFour.svg';
import CircleNumberOneIcon from '../../components/icons/custom/CircleNumberOne.svg';
import CircleNumberSixIcon from '../../components/icons/custom/CircleNumberSix.svg';
import CircleNumberThreeIcon from '../../components/icons/custom/CircleNumberThree.svg';
import CircleNumberTwoIcon from '../../components/icons/custom/CircleNumberTwo.svg';
import DraggerIcon from '../../components/icons/custom/Dragger.svg';
import FruitfulAccountIcon from '../../components/icons/custom/FruitfulAccount.svg';
import FruitfulLogoIcon from '../../components/icons/custom/FruitfulLogo.svg';
import MoneyMapIcon from '../../components/icons/custom/MoneyMap.svg';
import {
    FruitfulCustomIcon,
    type FruitfulCustomIconSize,
    FruitfulCustomIconSizeMap,
} from '../../components/icons/FruitfulCustomIcon';
import {
    FRUITFUL_ICON_KEY,
    SPACE_COLOR_KEYS,
} from '../../components/icons/FruitfulCustomIconDefaults';
import { Icon } from '../../components/icons/Icon';
import Switch from '../../components/inputs/Switch';
import { HolisticAccountLogo } from '../../components/logos/HolisticAccountLogo';
import { InstitutionLogo } from '../../components/logos/InstitutionLogo';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    LabelEmphasized,
    MonoBodySmallEmphasized,
} from '../../components/text/Typography';

export const Icons = () => {
    const [withShadow1, setWithShadow1] = useState(false);
    const [withShadow2, setWithShadow2] = useState(false);
    const [withShadow3, setWithShadow3] = useState(false);

    return (
        <YStack gap={48}>
            <YStack gap={8}>
                <BodyLargeEmphasized>Lucide Icons</BodyLargeEmphasized>
                <BodyMediumRegular>
                    Use our custom &lt;Icon /&gt; component for theme-compatible
                    colors
                </BodyMediumRegular>
                <XStack alignItems="center">
                    <Icon icon={BananaIcon} size={48} color="$marigold600" />
                    <Icon icon={GrapeIcon} size={36} color="$sweetViolet1100" />
                    <Icon icon={AppleIcon} size={28} color="$wasabi800" />
                    <Icon icon={CitrusIcon} size={26} color="$orange900" />
                    <Icon icon={CherryIcon} size={22} color="$red1000" />
                    <Icon icon={NutIcon} size={18} color="$dropletBlue1100" />
                </XStack>
                <BodyMediumRegular>
                    These icons accept any SVG props, including `strokeWidth`
                    and `fill`. But not all icons will handle `fill` properly.
                </BodyMediumRegular>
                <XStack alignItems="center">
                    <Icon
                        icon={BananaIcon}
                        size={48}
                        strokeWidth={1.3}
                        color="$marigold600"
                        fill="$marigold400"
                    />
                    <Icon
                        icon={GrapeIcon}
                        size={36}
                        strokeWidth={1.3}
                        color="$sweetViolet1100"
                        fill="$sweetViolet800"
                    />
                    <Icon
                        icon={AppleIcon}
                        size={28}
                        strokeWidth={1.3}
                        color="$wasabi800"
                        fill="$wasabi500"
                    />
                    <Icon
                        icon={CitrusIcon}
                        size={26}
                        strokeWidth={1.3}
                        color="$orange900"
                        fill="$orange500"
                    />
                    <Icon
                        icon={CherryIcon}
                        size={22}
                        strokeWidth={1.3}
                        color="$red1000"
                        fill="$red500"
                    />
                    <Icon
                        icon={NutIcon}
                        size={18}
                        strokeWidth={1.3}
                        color="$dropletBlue1100"
                        fill="$dropletBlue500"
                    />
                </XStack>
            </YStack>
            <YStack gap={8}>
                <BodyLargeEmphasized>
                    Fruitful Account Icons
                </BodyLargeEmphasized>
                <BodyMediumRegular>
                    Use our custom &lt;FruitfulCustomIcon /&gt; component for
                    bank account icons
                </BodyMediumRegular>
                <XStack alignItems="center" gap={6}>
                    <LabelEmphasized>Shadow</LabelEmphasized>
                    <Switch
                        isChecked={withShadow1}
                        onCheckedChange={setWithShadow1}
                    />
                </XStack>
                {Object.keys(FruitfulCustomIconSizeMap).map((size) => (
                    <FruitfulCustomIconRow
                        key={size}
                        size={size as FruitfulCustomIconSize}
                        withShadow={withShadow1}
                        useAltColors={false}
                    />
                ))}
                <BodyMediumRegular>
                    Investment accounts use &quot;alt&quot; colors for the icon
                </BodyMediumRegular>
                <FruitfulCustomIconRow
                    size="md"
                    withShadow={withShadow1}
                    useAltColors={true}
                />
            </YStack>
            <YStack gap={8}>
                <BodyLargeEmphasized>Institution Logos</BodyLargeEmphasized>
                <BodyMediumRegular>
                    Use our custom &lt;InstitutionLogo /&gt; component for
                    institution icons/logos
                </BodyMediumRegular>
                <XStack alignItems="center" gap={6}>
                    <LabelEmphasized>Shadow</LabelEmphasized>
                    <Switch
                        isChecked={withShadow2}
                        onCheckedChange={setWithShadow2}
                    />
                </XStack>
                <XStack gap={6}>
                    <InstitutionLogo
                        size="xxs"
                        showShadow={withShadow2}
                        logo="https://d1pwidzl9kib4u.cloudfront.net/institution-logos/fifth_third_bank.svg"
                    />
                    <InstitutionLogo
                        size="xs"
                        showShadow={withShadow2}
                        logo="https://d1pwidzl9kib4u.cloudfront.net/institution-logos/beal_bank_personal.svg"
                    />
                    <InstitutionLogo
                        size="sm"
                        showShadow={withShadow2}
                        logo="https://d1pwidzl9kib4u.cloudfront.net/institution-logos/truist.svg"
                    />
                    <InstitutionLogo
                        size="md"
                        showShadow={withShadow2}
                        logo="https://d1pwidzl9kib4u.cloudfront.net/institution-logos/bok_financial.svg"
                    />
                    <InstitutionLogo
                        size="lg"
                        showShadow={withShadow2}
                        logo="https://cdn.quiltt.io/institutions/port_13xSBodWiuO3b9YIGsCJc5T/logo.png"
                    />
                    {/* No logo provided -- shows fallback/default */}
                    <InstitutionLogo size="lg" showShadow={withShadow2} />
                    <InstitutionLogo size="md" showShadow={withShadow2} />
                    <InstitutionLogo size="sm" showShadow={withShadow2} />
                    <InstitutionLogo size="xs" showShadow={withShadow2} />
                    <InstitutionLogo size="xxs" showShadow={withShadow2} />
                </XStack>
            </YStack>
            <YStack gap={8}>
                <BodyLargeEmphasized>
                    Universal Icon/Logo component: HolisticAccountLogo
                </BodyLargeEmphasized>
                <BodyMediumRegular>
                    Use our custom &lt;HolisticAccountLogo /&gt; component when
                    working with accounts that may be mixed internal/external
                    accounts.
                </BodyMediumRegular>
                <XStack alignItems="center" gap={6}>
                    <LabelEmphasized>Shadow</LabelEmphasized>
                    <Switch
                        isChecked={withShadow3}
                        onCheckedChange={setWithShadow3}
                    />
                </XStack>
                <XStack gap={6}>
                    <HolisticAccountLogo
                        size="sm"
                        showShadow={withShadow3}
                        institutionName="Arvest"
                        logo="https://d1pwidzl9kib4u.cloudfront.net/institution-logos/arvest_bank_online_banking.svg"
                    />
                    <HolisticAccountLogo
                        size="md"
                        showShadow={withShadow3}
                        institutionName="Bank OZK"
                        logo="https://d1pwidzl9kib4u.cloudfront.net/institution-logos/bank_ozk_personal.svg"
                    />
                    <HolisticAccountLogo
                        size="lg"
                        showShadow={withShadow3}
                        institutionName="Citizens Bank"
                        logo="https://d1pwidzl9kib4u.cloudfront.net/institution-logos/citizens_bank_amsterdam_mo.svg"
                    />
                    <HolisticAccountLogo
                        size="lg"
                        showShadow={withShadow3}
                        institutionName={FRUITFUL_INSTITUTION_NAME}
                        logo="globe:green"
                    />
                    <HolisticAccountLogo
                        size="md"
                        showShadow={withShadow3}
                        institutionName={FRUITFUL_INSTITUTION_NAME}
                        logo="gamingPad:red"
                    />
                    <HolisticAccountLogo
                        size="sm"
                        showShadow={withShadow3}
                        institutionName={FRUITFUL_INSTITUTION_NAME}
                        logo="diamond:blue"
                    />
                </XStack>
            </YStack>
            <YStack gap={8}>
                <BodyLargeEmphasized>Custom SVG Icons</BodyLargeEmphasized>
                <BodyMediumRegular>
                    Custom SVG icons imported from the custom directory. Use the
                    &lt;Icon /&gt; component with these icons.
                </BodyMediumRegular>
                <YStack gap={16}>
                    <YStack gap={8}>
                        <BodyMediumRegular>
                            Circle Number Icons
                        </BodyMediumRegular>
                        <XStack alignItems="center" gap={12} flexWrap="wrap">
                            <Icon icon={CircleNumberOneIcon} />
                            <Icon icon={CircleNumberTwoIcon} />
                            <Icon icon={CircleNumberThreeIcon} />
                            <Icon icon={CircleNumberFourIcon} />
                            <Icon icon={CircleNumberFiveIcon} />
                            <Icon icon={CircleNumberSixIcon} />
                        </XStack>
                    </YStack>
                    <YStack gap={8}>
                        <BodyMediumRegular>
                            Other Custom Icons
                        </BodyMediumRegular>
                        <XStack alignItems="center" gap={12} flexWrap="wrap">
                            <Icon icon={CalendarDollarSignIcon} />
                            <Icon icon={DraggerIcon} />
                            <Icon icon={FruitfulAccountIcon} />
                            <Icon icon={FruitfulLogoIcon} />
                            <Icon icon={MoneyMapIcon} />
                            <Icon icon={MoneySystemIcon} />
                        </XStack>
                    </YStack>
                    <YStack gap={8}>
                        <BodyMediumRegular>
                            Custom SVG Icons with Different Sizes
                        </BodyMediumRegular>
                        <XStack alignItems="center" gap={12}>
                            <Icon icon={MoneyMapIcon} size={18} />
                            <Icon icon={MoneyMapIcon} size={24} />
                            <Icon icon={MoneyMapIcon} size={32} />
                            <Icon icon={MoneyMapIcon} size={48} />
                            <Icon icon={MoneyMapIcon} size={64} />
                        </XStack>
                    </YStack>
                    <YStack gap={8}>
                        <BodyMediumRegular>
                            Custom SVG Icons with Theme Colors
                        </BodyMediumRegular>
                        <XStack alignItems="center" gap={12} flexWrap="wrap">
                            <Icon
                                icon={CalendarDollarSignIcon}
                                color="$green900"
                            />
                            <Icon icon={DraggerIcon} color="$red900" />
                            <Icon
                                icon={FruitfulAccountIcon}
                                color="$orange900"
                            />
                            <Icon
                                icon={MoneyMapIcon}
                                color="$dropletBlue1100"
                            />
                        </XStack>
                    </YStack>
                </YStack>
            </YStack>
        </YStack>
    );
};

const FruitfulCustomIconRow = ({
    size,
    withShadow,
    useAltColors,
}: {
    size: FruitfulCustomIconSize;
    withShadow: boolean;
    useAltColors: boolean;
}) => {
    return (
        <XStack alignItems="center" gap={4}>
            <MonoBodySmallEmphasized
                width={80}
                textAlign="right"
                paddingRight={8}
            >
                {size} ({FruitfulCustomIconSizeMap[size]})
            </MonoBodySmallEmphasized>
            <FruitfulCustomIcon
                size={size}
                icon="fruitful"
                color={SPACE_COLOR_KEYS.GREEN}
                showShadow={withShadow}
                useAltColors={useAltColors}
            />
            <FruitfulCustomIcon
                size={size}
                icon={FRUITFUL_ICON_KEY}
                color={SPACE_COLOR_KEYS.BLUE}
                showShadow={withShadow}
                useAltColors={useAltColors}
            />
            <FruitfulCustomIcon
                size={size}
                icon="gamingPad"
                color={SPACE_COLOR_KEYS.PURPLE}
                showShadow={withShadow}
                useAltColors={useAltColors}
            />
            <FruitfulCustomIcon
                size={size}
                icon="diamond"
                color={SPACE_COLOR_KEYS.RED}
                showShadow={withShadow}
                useAltColors={useAltColors}
            />
            <FruitfulCustomIcon
                size={size}
                icon="tv"
                color={SPACE_COLOR_KEYS.ORANGE}
                showShadow={withShadow}
                useAltColors={useAltColors}
            />
            <FruitfulCustomIcon
                size={size}
                icon="globe"
                color={SPACE_COLOR_KEYS.PEACH}
                showShadow={withShadow}
                useAltColors={useAltColors}
            />
        </XStack>
    );
};
